import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Modal, Text } from '@Sage/hoi-poi-ui';
import EntityLink from 'components/EntityLink';

import MenuHoi from 'components/MenuHoi';
import { Dots, SalesorderDefault, SalesorderLost, SalesorderWon } from 'components/SvgIcons';
import colors from 'constants/colors';
import { COMPANIES, OPPORTUNITIES, SALESORDERS } from 'constants/Entities';
import { CompanyChip, OpportunityChip, UserChip } from 'containers/components/chips';
import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import { ENTITY_MODAL_UPDATE } from 'lib/events';
import { publish } from 'lib/EventBuser';
import Context from 'managers/Context';
import { EntityModalActions, OrdersActions } from 'actions';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getDetailRoute } from 'utils/fm';
import { getLiteral } from 'utils/getLiteral';
import { canViewEntity } from 'utils/entityPermissions';
import { stopEntityNavigation } from 'utils/entityNavigation';
import { errorToast, successToast } from 'utils/toast';
import { logEvent } from 'utils/tracking';

const SalesOrdersWidgetRow = memo(({ data, entityType, readonly, refreshSalesOrders }) => {
    const dispatch = useDispatch();
    const crudPermissions = useSelector(
        (state) => state.config.permission.crud_permission.SALESORDERS,
    );
    const duplicatePermission = useSelector(
        (state) => state.config.permission.duplicateSalesOrders,
    );
    const { hasTabs: withTabs, openTab } = useEntityDetail();

    const [isDuplicating, setIsDuplicating] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const duplicateOrder = useCallback(() => {
        if (isDuplicating) return;
        if (!canViewEntity(SALESORDERS)) return;

        setIsDuplicating(true);
        dispatch(OrdersActions.duplicateEntity(data.id))
            .then((result) => {
                window.location.href = getDetailRoute({ entity: SALESORDERS, id: result.id });
            })
            .catch(() => {
                setIsDuplicating(false);
            });
    }, [data.id, dispatch, isDuplicating]);

    const editOrder = useCallback(() => {
        logEvent({
            event: entityType.trueName,
            submodule: 'salesOrdersWidget',
            functionality: 'edit',
        });

        dispatch(
            EntityModalActions.init({
                entity: SALESORDERS,
                id: data.id,
                labels: {
                    title: getLiteral('label_editing_salesorders'),
                    success: getLiteral('succes_entityupdatedsuccessfully'),
                    error: getLiteral('error_generalerror'),
                },
                crudTab: SALESORDERS,
                hideDelete: true,
                disableConfirm: !crudPermissions.update,
                size: 'big',
            }),
        );
    }, [crudPermissions.update, data.id, dispatch, entityType.trueName]);

    const openDeleteModal = useCallback(() => {
        setIsDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setIsDeleteModalOpen(false);
    }, []);

    const confirmDelete = useCallback(() => {
        if (isDeleting) return;

        setIsDeleting(true);

        Context.domainManager.deleteEntity(
            SALESORDERS.entity,
            data.id,
            () => {
                successToast({ text: getLiteral('succes_entitydeletedsuccessfully') });
                publish(`${ENTITY_MODAL_UPDATE}--${SALESORDERS.entity}`);
                closeDeleteModal();
                setIsDeleting(false);
            },
            (error) => {
                console.error(error);
                errorToast({ text: getLiteral('error_generalerror') });
                setIsDeleting(false);
                setIsDeleteModalOpen(false);
            },
        );
    }, [closeDeleteModal, data.id, isDeleting]);

    const setAsPrincipal = useCallback(() => {
        if (!((entityType === OPPORTUNITIES && !data.principal) || crudPermissions.create)) return;

        Context.entityManager
            .getEntitiesManager(SALESORDERS)
            .setAsPrincipal(data.id, !data.principal, () => {
                refreshSalesOrders(data.id, !data.principal);
            });
    }, [crudPermissions.create, data.id, data.principal, entityType, refreshSalesOrders]);

    const onClickOpportunity = useCallback(
        (e) => {
            if (!canViewEntity(OPPORTUNITIES)) return stopEntityNavigation(e);
            e.preventDefault();
            openTab({
                entity: OPPORTUNITIES,
                id: data.idOpportunity,
                hasCrudInDetail: true,
            });
        },
        [data.idOpportunity, openTab],
    );

    const onClickCompany = useCallback(
        (e) => {
            if (!canViewEntity(COMPANIES)) return stopEntityNavigation(e);
            e.preventDefault();
            openTab({
                entity: COMPANIES,
                id: data.idCompany,
                hasCrudInDetail: true,
            });
        },
        [data.idCompany, openTab],
    );

    const onClickSalesOrder = useCallback(
        (e) => {
            if (!canViewEntity(SALESORDERS)) return stopEntityNavigation(e);
            e.preventDefault();
            openTab({
                entity: SALESORDERS,
                id: data.id,
                hasCrudInDetail: true,
            });
        },
        [data.id, openTab],
    );

    const dataFiltered = useMemo(() => {
        let orderReference = '-';
        let extraEntity = '-';
        let externalAnchor;
        let externalOnClick = null;

        switch (entityType) {
            case COMPANIES:
                orderReference = data.reference ?? '-';
                externalOnClick = withTabs ? onClickOpportunity : null;
                extraEntity = data.opportunity ? (
                    <OpportunityChip
                        idOpportunity={data.idOpportunity}
                        nameOpportunity={data.opportunity}
                        remotePopover={true}
                        onClickPopover={externalOnClick}
                    />
                ) : (
                    '-'
                );
                externalAnchor =
                    data.idOpportunity !== -1 && canViewEntity(OPPORTUNITIES)
                        ? getDetailRoute({ entity: OPPORTUNITIES, id: data.idOpportunity })
                        : null;
                externalOnClick = onClickOpportunity;
                break;
            case OPPORTUNITIES:
                orderReference = data.reference ?? '-';
                externalOnClick = withTabs ? onClickCompany : null;
                extraEntity = data.company ? (
                    <CompanyChip
                        idCompany={data.idCompany}
                        nameCompany={data.company}
                        remotePopover={true}
                        onClickPopover={externalOnClick}
                    />
                ) : (
                    '-'
                );
                externalAnchor =
                    data.idCompany !== -1 && canViewEntity(COMPANIES)
                        ? getDetailRoute({ entity: COMPANIES, id: data.idCompany })
                        : null;
                break;
        }

        return {
            orderReference,
            extraEntity,
            externalAnchor,
            externalOnClick,
        };
    }, [
        data.company,
        data.idCompany,
        data.idOpportunity,
        data.opportunity,
        data.reference,
        entityType,
        onClickCompany,
        onClickOpportunity,
        withTabs,
    ]);

    const icon = useMemo(() => {
        if (data.win) return <SalesorderWon color={colors.actionMinor[200]} />;
        if (data.lost) return <SalesorderLost color={colors.actionMinor[200]} />;
        return <SalesorderDefault color={colors.actionMinor[200]} />;
    }, [data.lost, data.win]);

    const extraEntity = useMemo(() => {
        return dataFiltered.externalAnchor ? (
            <a href={dataFiltered.externalAnchor} onClick={dataFiltered.externalOnClick}>
                {dataFiltered.extraEntity}
            </a>
        ) : (
            <div className="text">{dataFiltered.extraEntity}</div>
        );
    }, [dataFiltered.externalAnchor, dataFiltered.externalOnClick, dataFiltered.extraEntity]);

    const principalBadge = useMemo(() => {
        return entityType === OPPORTUNITIES && data.principal ? (
            <Badge type="semanticFocus" variant="inverted">
                {getLiteral('label_principal')}
            </Badge>
        ) : null;
    }, [data.principal, entityType]);

    const menuItems = useMemo(() => {
        if (readonly) return [];

        const items = [];

        if (entityType === OPPORTUNITIES && !data.principal && crudPermissions.create) {
            items.push({
                title: <Text>{getLiteral('label_makeprincipal')}</Text>,
                onClick: setAsPrincipal,
            });
        }

        if (duplicatePermission) {
            items.push({
                title: <Text>{getLiteral('action_duply')}</Text>,
                onClick: duplicateOrder,
                disabled: isDuplicating,
            });
        }

        if (crudPermissions.update) {
            items.push({
                title: <Text>{getLiteral('action_edit')}</Text>,
                onClick: editOrder,
            });
        }

        if (crudPermissions.delete) {
            items.push({
                title: <Text>{getLiteral('action_delete')}</Text>,
                onClick: openDeleteModal,
                disabled: isDeleting,
            });
        }

        return items;
    }, [
        crudPermissions.create,
        crudPermissions.delete,
        crudPermissions.update,
        data.principal,
        readonly,
        duplicateOrder,
        duplicatePermission,
        editOrder,
        entityType,
        isDuplicating,
        isDeleting,
        openDeleteModal,
        setAsPrincipal,
    ]);

    const responsible = useMemo(() => data.responsible ?? '-', [data.responsible]);
    const state = useMemo(() => data.stateSalesOrder ?? '-', [data.stateSalesOrder]);
    const amount = useMemo(() => {
        return (
            data.amountSalesOrder &&
            formatDecimalByConfig({
                value: data.amountSalesOrder,
                type: DECIMAL_TYPES.CURRENCY_GENERAL,
                idEnvironment: data.idBranch,
                idCurrency: data.idCurrency,
                currencySymbol: data.currencySymbol,
                entityName: SALESORDERS.extraFieldName,
            })
        );
    }, [data]);

    const containerClasses = useMemo(() => {
        const classes = ['sales-orders-widget-row-container'];
        if (entityType === OPPORTUNITIES && data.principal) {
            classes.push('principal');
        }
        return classes.join(' ');
    }, [data.principal, entityType]);

    const modalDeleteOverrides = useMemo(
        () => ({
            confirmButton: {
                isLoading: isDeleting,
                type: 'primary-error',
            },
        }),
        [isDeleting],
    );

    return (
        <>
            <div className={containerClasses}>
                <div className="sales-orders-widget-row">
                    <div className="sales-orders-widget-row-left">
                        <div className="row-icon-box">
                            <div className="row-left__icon">{icon}</div>
                        </div>
                        <div className="row-info-box">
                            <div className="row-left__name">
                                <EntityLink
                                    entity={SALESORDERS}
                                    href={getDetailRoute({ entity: SALESORDERS, id: data.id })}
                                    onClick={withTabs ? onClickSalesOrder : undefined}
                                    linkProps={{ target: '_blank', rel: 'noreferrer' }}
                                    textProps={{}}
                                >
                                    {data.salesOrderNumber || '-'}
                                </EntityLink>
                                {principalBadge}
                            </div>
                            <div className="row-left__own-entity">
                                {dataFiltered.orderReference || '-'}
                            </div>
                            <div className="row-left__external-entity">{extraEntity}</div>
                        </div>
                    </div>
                    <div className="sales-orders-widget-row-right">
                        <div className="row-right__content">
                            <div className="content-amount">{amount || '-'}</div>
                            <div className="content-state">{state}</div>
                            <div className="content-responsible">
                                <UserChip
                                    idUser={data.idResponsible}
                                    username={responsible}
                                    remotePopover={true}
                                />
                            </div>
                        </div>
                        <div className="row-right__actions">
                            {menuItems.length > 0 && (
                                <MenuHoi items={menuItems}>
                                    <Dots width="18px" height="18px" />
                                </MenuHoi>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                title={getLiteral('success_salesorders_entity_deleted')}
                isOpen={isDeleteModalOpen}
                onRequestClose={closeDeleteModal}
                onCancel={closeDeleteModal}
                onConfirm={confirmDelete}
                confirmText={getLiteral('helptext_confirm')}
                cancelText={getLiteral('action_cancel')}
                size="small"
                overrides={modalDeleteOverrides}
            >
                <Text>{getLiteral('confirm_delete')}</Text>
            </Modal>
        </>
    );
});

SalesOrdersWidgetRow.displayName = 'SalesOrdersWidgetRow';

export default SalesOrdersWidgetRow;
